<template>
  <div id="container">
    <Legend v-bind:data-types="dataTypes" left="1450" />
    <Legend v-bind:data-types="dataTypes" left="7900" />
    <Legend v-bind:data-types="dataTypes" left="10200" />
    <Legend v-bind:data-types="dataTypes" left="11000" />
    <div id="bg" />
    <Timeline />
    <IntroSection />
    <HistorySection v-if="addScrollTarget" :addScrollTarget="addScrollTarget" />
    <WildlifeSection />
    <MapSection />
    <TimelineTwo v-if="xScale" :x-scale="xScale" :dates="cultureData" />
    <CultureSection v-if="xScale && cultureData" :data="cultureData" :x-scale="xScale" />
    <JetportSection
      v-if="xScale && addScrollTarget"
      :key-dates="keyDates"
      :x-scale="xScale"
      :addScrollTarget="addScrollTarget"
    />
    <HydrologySection v-if="xScale" :x-scale="xScale" />
    <FutureSection />
  </div>
</template>

<script>
/* eslint-disable */
import { scaleLinear, select } from 'd3'
import _ from 'lodash'
import Legend from './components/Legend.vue'
import Timeline from './components/Timeline.vue'
import HydrologySection from './components/HydrologySection.vue'
import IntroSection from './components/IntroSection.vue'
import HistorySection from './components/HistorySection.vue'
import WildlifeSection from './components/WildlifeSection.vue'
import MapSection from './components/MapSection.vue'
import CultureSection from './components/CultureSection.vue'
import TimelineTwo from './components/TimelineTwo.vue'
import JetportSection from './components/JetportSection.vue'
import FutureSection from './components/FutureSection.vue'
import { initiateIntersectionObserver } from './scrollEvents'
export default {
  components: {
    Legend,
    Timeline,
    HydrologySection,
    IntroSection,
    HistorySection,
    WildlifeSection,
    MapSection,
    CultureSection,
    TimelineTwo,
    JetportSection,
    FutureSection,
  },
  data: () => ({
    addScrollTarget: null,
    dataTypes: [
      {
        name: 'Jetport',
        color: '#74438C',
        outline: '#8f73a4',
      },
      {
        name: 'Swamp Music',
        color: '#D08E14',
        outline: '#d6b167',
      },
      {
        name: 'Swamp Films',
        color: '#754C24',
        outline: '#927a5e',
      },
      {
        name: 'Key Dates',
        color: '#EC008C',
        outline: '#e26eb7',
      },
      {
        name: 'Hydrology',
        color: '#6DA5C6',
        outline: '#a1bed5',
      },
      {
        name: 'Wildlife/Habitat',
        color: '#6F922A',
        outline: '#98ac68',
      },
    ],
    cultureData: [
      {
        year: 1939,
        iframeSrc:
          'https://open.spotify.com/embed/track/4PN5gbJNYWx9yQ6hJepPbG',
        style: {
          left: 200,
          top: 140,
          adjustHorizontal: -100,
        },
        pointerStyle: {
          height: 80,
          direction: 'down',
        },
      },
      {
        year: 1946,
        film: {
          title: 'Strangler of the Swamp',
          imgSrc: 'img/StrangleroftheSwamp.png',
          link: 'https://www.imdb.com/title/tt0038129/?ref_=nv_sr_srsg_0',
        },
        style: {
          left: 500,
          top: 150,
          adjustHorizontal: -160,
        },
        pointerStyle: {
          height: 100,
          direction: 'down',
        },
      },
      {
        year: 1954,
        film: {
          title: 'Creature from the Black Lagoon',
          imgSrc: 'img/creatures.gif',
          link: 'https://www.imdb.com/title/tt0046876/?ref_=fn_al_tt_1',
        },
        style: {
          left: 1025,
          top: 110,
          adjustHorizontal: -25,
          width: 250,
        },
        pointerStyle: {
          height: 60,
          direction: 'down',
        },
      },
      {
        year: 1956,
        keyDate: {
          title: _.upperCase(
            'The State Mosquito Control Commission (SMCC) is established'
          ),
          text:
            'A common misconception is that to completely eradicate mosquitos will eliminate disease.  However, its later found that a healthy wetland provides a natural habitat for many unique animals including natural enemies of mosquitoes.',
        },
        style: {
          top: 770,
          left: 1400,
          bottom: '40px',
          width: 500,
          backgroundColor: 'white',
        },
        pointerStyle: {
          height: 160,
          direction: 'down',
        },
      },
      {
        year: 1959,
        film: {
          title: 'Alligator People',
          imgSrc: 'img/alligator.gif',
          link: 'https://www.imdb.com/title/tt0052549/',
        },
        style: {
          left: 2000,
          width: 300,
          top: 75,
          adjustHorizontal: -50,
        },
        pointerStyle: {
          height: 35,
        },
      },
      {
        year: 1959,
        film: {
          title: 'Attack of the Giant Leeches',
          imgSrc: 'img/AttackoftheGiantLeeches.png',
          link: 'https://www.imdb.com/title/tt0053611/?ref_=fn_al_tt_1',
        },
        style: {
          left: 2000,
          top: 250,
          adjustHorizontal: -80,
        },
        pointerStyle: {
          direction: 'up',
          height: 125,
        },
      },
      {
        year: 1960,
        style: {
          left: 2200,
        },
        pointerStyle: {
          height: 35,
          direction: 'up',
        },
        jetport: true,
      },
      {
        year: 1964,
        keyDate: {
          title: _.upperCase(
            'The Great Swamp National Wildlife Refuge and Wilderness Area is established.'
          ),
          text: `The first designation in the United State Refuge system.`,
        },
        style: {
          left: 2920,
          top: 375,
          width: 600,
          adjustHorizontal: 30,
        },
      },
      {
        year: 1964,
        keyDate: {
          title: _.upperCase('The plan to build the jetport is defeated.'),
          text: `The preservation effort succeeds.  The Great Swamp National Wildlife Refuge is dedicated to the U.S. Department of the Interior.`,
        },
        style: {
          color: 'var(--jetport-purple)',
          left: 2900,
          top: 200,
          width: 600,
          adjustHorizontal: -155,
        },
        pointerStyle: {
          height: 130,
          direction: 'up',
        },
        jetport: true,
      },

      {
        year: 1966,
        film: {
          title: 'Curse of the Swamp Creature',
          imgSrc: 'img/CurseoftheSwampCreature.png',
          link: 'https://www.imdb.com/title/tt0060270/?ref_=nv_sr_srsg_0',
        },
        style: {
          left: 3300,
          top: 125,
          width: 290,
          adjustHorizontal: -250,
        },
        pointerStyle: {
          height: 85,
        },
      },
      {
        year: 1968,
        keyDate: {
          title: _.upperCase(
            'The Great Swamp becomes the first Fish and WildlifeService wilderness to be added to the National Wildlife Preservation System.'
          ),
          text: `National Wilderness Areas are the highest<br>protected status any land in the U.S. can have.`,
        },
        style: {
          top: 75,
          left: 3650,
          width: 550,
          adjustHorizontal: -300,
        },
        pointerStyle: {
          direction: 'up',
          height: 220,
        },
      },
      {
        year: 1969,
        iframeSrc:
          'https://open.spotify.com/embed/track/5bUlFE9dGh7pX93PUEVAue?si=zZCnZXYfR86sJUYOLKygEw',
        style: {
          left: 3850,
          top: 140,
          adjustHorizontal: -100,
        },
        pointerStyle: {
          height: 80,
        },
      },
      {
        year: 1970,
        keyDate: {
          title: _.upperCase('The Federal Clean Air Act passes (CAA)'),
          text: `This legislation authorized the development of comprehensive federal and state regulations to<br> limit emissions from both stationary (industrial)<br> and mobile sources.`,
        },
        style: {
          left: 4060,
          top: 225,
          width: 500,
          adjustHorizontal: -175,
        },
        pointerStyle: {
          height: 100,
          direction: 'up',
        },
      },
      {
        year: 1972,
        keyDate: {
          title: _.upperCase('The Federal Clean Water Act passes (CWA)'),
          text: `Its objective is to restore and maintain the<br> chemical, physical, and biological integrity of<br> the nation's waters; including maintaining the<br> integrity of wetlands. The Clean Water Act was<br> one of the United States' first and most influential modern environmental laws.`,
        },
        style: {
          left: 4420,
          width: 550,
        },
        pointerStyle: {
          height: 370,
          direction: 'up',
        },
      },
      {
        year: 1972,
        film: {
          title: 'Frogs',
          imgSrc: 'img/frog.gif',
          link: 'https://www.imdb.com/title/tt0068615/?ref_=fn_al_tt_1',
        },
        style: {
          left: 4400,
          top: 135,
          width: 400,
          adjustHorizontal: -200,
        },
        pointerStyle: {
          height: 125,
        },
      },
      {
        year: 1976,
        iframeSrc:
          'https://open.spotify.com/embed/track/4c1QKtG7VHmhlOwJcA3Zcy?si=xmWB_pBsQ6KsIWTWVUEcYw',
        style: {
          left: 5200,
          top: 150,
          adjustHorizontal: -100,
        },
        pointerStyle: {
          height: 75,
        },
      },
      {
        year: 1979,
        style: {
          left: 5800,
        },
      },

      {
        year: 1982,
        style: {
          left: 5800 + 3 * 91.25,
        },
      },
      {
        year: 1985,
        style: {
          left: 5800 + 6 * 91.25,
        },
      },
      {
        year: 1987,
        style: {
          left: 5800 + 8 * 91.25,
        },
      },
      {
        year: 1992,
        style: {
          left: 5800 + 13 * 91.25,
        },
      },
      {
        year: 1997,
        style: {
          left: 5800 + 18 * 91.25,
        },
      },
      {
        year: 2002,
        style: {
          left: 5800 + 23 * 91.25,
        },
      },
      {
        year: 2007,
        style: {
          left: 5800 + 28 * 91.25,
        },
      },
      {
        year: 2012,
        style: {
          left: 5800 + 33 * 91.25,
        },
      },
      // {
      //   year: 2015,
      //   iframeSrc:
      //     'https://open.spotify.com/embed/track/0K1Npq5lQIp80BTMIQ2mVd?si=uuTd0gw5TcyWqK0aBrrjKQ',
      //   style: {
      //     left: 5800 + 36 * 91.25,
      //     adjustHorizontal: -175,
      //     top: 150,
      //   },
      //   pointerStyle: {
      //     height: 75,
      //   },
      // },
      // {
      //   year: 2016,
      //   iframeSrc:
      //     'https://open.spotify.com/embed/track/6zwdorhTZt1ZF3jyMzGtP1?si=NoVQ_TcNQZO9f-mBz68aWg',
      //   style: {
      //     left: 5800 + 37 * 91.25,
      //     top: 150,
      //     adjustHorizontal: -10,
      //   },
      //   pointerStyle: {
      //     height: 75,
      //   },
      // },
      //{
      //  year: 2019,
      //  film: {
      //   title: 'Swamp People',
      //    imgSrc: 'img/swamppeople.gif',
      //    link: 'https://www.imdb.com/title/tt1747551/',
      //  },
      //  style: {
      //    left: 9450,
      //    top: 100,
      //    adjustHorizontal: -50,
      //    width: 275,
      //  },
      //  pointerStyle: {
      //    height: 50,
      //  },
      //},
      {
        year: 2020,
        style: {
          left: 9570,
        },
      },
      {
        year: 2025,
        style: {
          left: 9850,
        },
      },
    ],
    xScale: null,
  }),
  mounted() {
    const scrollConverter = createScrollConverter(window, document)
    scrollConverter.activate()
    this.generatexScale()
    this.addScrollTarget = initiateIntersectionObserver()
  },
  computed: {
    keyDates: function() {
      return _.filter(this.cultureData, d => !!d.keyDate)
    },
  },
  methods: {
    generatexScale() {
      const xScale = scaleLinear()
        .domain([1938, 2024])
        .range([0, 10350])

      this.xScale = xScale
    },
  },
}

/*
scrollConverter 1.0.4
https://github.com/koggdal/scroll-converter
``
Copyright 2011–2020 Johannes Koggdal (http://koggdal.com/)
Developed for BombayWorks (http://bombayworks.com/)

Released under MIT license
*/

function createScrollConverter(e, t, n) {
  var o,
    r,
    a = t.documentElement,
    l = !1,
    i = !1,
    s = !1,
    c = function() {
      var t = !1
      try {
        var n = Object.defineProperty({}, 'passive', {
          get: function() {
            t = !0
          },
        })
        e.addEventListener('test', null, n),
          e.removeEventListener('test', null, n)
      } catch (e) {}
      return t
    },
    v = function(n) {
      var o,
        r = 'scroll' + (n = n.toUpperCase()),
        a = 'scroll' + ('X' === n ? 'Left' : 'Top')
      return (
        e['page' + n + 'Offset'] ||
        e[r] ||
        ('number' == typeof (o = t.documentElement || t.body.parentNode)[a]
          ? o
          : t.body)[a]
      )
    },
    d = function(n, i) {
      if (
        ((o = function(o) {
          if (
            ((o = o || e.event),
            !1 ===
              (function(n, o, r) {
                return (
                  !l ||
                  ((i = 0),
                  (v = (a ? a.offsetWidth : 0) || 0),
                  (d = t.body.scrollWidth || 0),
                  (u = a ? a.clientWidth : 0),
                  (f = Math.max(v, d) - u),
                  Math.abs(o.wheelDeltaX) >= Math.abs(o.wheelDeltaY) ||
                    Math.abs(o.deltaX) >= Math.abs(o.deltaY) ||
                    ('deltaY' in o
                      ? (i = -10 * o.deltaY)
                      : o.detail
                      ? (i = -240 * o.detail)
                      : o.wheelDelta && (i = 5 * o.wheelDelta),
                    (s = (i / 120) * 10),
                    (c = n.x - s) >= 0 && c <= f
                      ? ((n.x = c), (n.setByScript = !0), e.scrollTo(n.x, n.y))
                      : 0 !== n.x &&
                        n.x !== f &&
                        ((n.x = c > f ? f : 0),
                        (n.setByScript = !0),
                        e.scrollTo(n.x, n.y)),
                    'function' == typeof r && r(n),
                    !1))
                )
                var i, s, c, v, d, u, f
              })(n, o, i))
          ) {
            if (!o.preventDefault || !o.stopPropagation) return !1
            o.preventDefault(), o.stopPropagation()
          }
        }),
        (r = function() {
          n.setByScript || ((n.x = v('x')), (n.y = v('y'))),
            (n.setByScript = !1)
        }),
        e.addEventListener)
      ) {
        var s = !!c() && { capture: !1, passive: !1 }
        'onwheel' in e
          ? e.addEventListener('wheel', o, s)
          : 'onmousewheel' in e
          ? e.addEventListener('mousewheel', o, s)
          : e.addEventListener('DOMMouseScroll', o, s),
          e.addEventListener('scroll', r, s)
      } else t.attachEvent('onmousewheel', o), e.attachEvent('onscroll', r)
    },
    u = function(e) {
      return e.preventDefault(), e.stopPropagation(), !1
    }
  return {
    activate: function(t) {
      if (((l = !0), !s)) {
        d({ x: 0, y: 0 }, t), (s = !0)
      }
      if (i) {
        if (e.addEventListener) {
          var n = !c() || { capture: !0, passive: !1 }
          e.removeEventListener('scroll', u, n)
        } else e.detachEvent('onscroll', u)
        i = !1
      }
    },
    deactivate: function() {
      ;(l = !1),
        s &&
          (!(function() {
            if (o || r)
              if (e.removeEventListener) {
                var n = !!c() && { capture: !1, passive: !1 }
                'onwheel' in e
                  ? e.removeEventListener('wheel', o, n)
                  : 'onmousewheel' in e
                  ? e.removeEventListener('mousewheel', o, n)
                  : e.removeEventListener('DOMMouseScroll', o, n),
                  e.removeEventListener('scroll', r, n)
              } else
                t.detachEvent('onmousewheel', o), e.detachEvent('onscroll', r)
          })(),
          (s = !1))
    },
    deactivateAllScrolling: function() {
      if (((l = !1), (i = !0), e.addEventListener)) {
        var t = !c() || { capture: !0, passive: !1 }
        e.addEventListener('scroll', u, t)
      } else e.attachEvent('onscroll', u)
    },
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css?family=Open+Sans:300,300i,400,600,700&display=swap');
@import url('https://fonts.googleapis.com/css?family=Playfair+Display&display=swap');
:root {
  --grey-text: #636162;
  --green-text: #638436;
  --timeline-green: #aebf94;
  --dark-green-text: #3e3d1c;
  --blue-text: #416f8d;
  --jetport-purple: #74438c;
  --jetport-purple-outline: #8f73a4;
  --music-yellow: #d08e14;
  --music-yellow-outline: #d6b167;
  --film-brown: #754c24;
  --film-brown-outline: #927a5e;
  --key-date-pink: #ec008c;
  --key-date-pink-outline: #e26eb7;
  --hydrology-blue: #6da5c6;
  --hydrology-blue-outline: #a1bed5;
  --wildlife-green: #6f922a;
  --wildlife-green-outline: #98ac68;
  --generic-grey: #808080;
  font-family: 'Open Sans', sans-serif;
}

#container {
  margin: 0 auto;
  min-height: 100vh;
  max-height: 100vh;
  overflow-y: hidden;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  width: 16030px;
  -webkit-font-smoothing: antialiased;
}

#container h3 {
  text-align: left;
  text-transform: uppercase;
  font-size: 27pt;
  font-weight: 600;
}

#container h5 {
  font-size: 18pt;
  margin-top: 0;
  margin-bottom: 0;
}

#container p {
  font-size: 14pt;
  line-height: 25pt;
}
#container .time-marker {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
}
#container .time-marker .circle {
  width: 15px;
  height: 15px;
  background-color: grey;
  border-radius: 50%;
  position: relative;
  top: -8.25px;
}
#container .time-marker span {
  font-family: 'Playfair Display', serif;
  font-size: 15pt;
  color: var(--generic-grey);
}

.title {
  font-family: 'Quicksand', 'Source Sans Pro', -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  display: block;
  font-weight: 300;
  font-size: 100px;
  color: #35495e;
  letter-spacing: 1px;
}

.subtitle {
  font-weight: 300;
  font-size: 42px;
  color: #526488;
  word-spacing: 5px;
  padding-bottom: 15px;
}

.links {
  padding-top: 15px;
}
#bg {
  position: absolute;
  top: 0px;
  left: 5400px;
  width: 6000px;
  height: 100vh;
  /* background: url('./assets/swamp-bg.png'); */
  background-size: cover;
  background-repeat: repeat-x;
}
</style>
